import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Collapse, Col, Row } from 'reactstrap';

import { ReactComponent as FolderIcon } from 'assets/icon-microsoft-folder-closed.svg';
import { ReactComponent as OpenFolderIcon } from 'assets/icon-microsoft-folder-open.svg';
import { ReactComponent as CaretDownIcon } from 'assets/icon-angle-down.svg';
import { ReactComponent as CaretUpIcon } from 'assets/icon-angle-up.svg';

import { CustomFolderProps } from 'shared/prop-types/ReducerProps';

import useApi from 'hooks/useApi';
import { convertErrorsArrayToString } from 'shared/helpers/helper';
import { emptyString, normalizeString } from 'shared/utils';
import { TextField } from 'shared/components/form/Inputs';
import { ReactComponent as SearchIcon } from 'assets/icon-search.svg';
import { colors } from 'shared/defaultTheme';
import { Button } from 'shared/components/Buttons';
import { CustomFoldersDataWrapper, CustomFolderTreeWrapper, RadioInput } from './styles';

const DEFAULT_FOLDER_ID = '2';

const SelectCustomFolder = ({ setFolderSelected, folderSelected, onClose }) => {
  const { customAreaApi } = useApi();
  const { t } = useTranslation();
  const [folderRoot, setFolderRoot] = useState({});
  const [folders, setFolders] = useState([]);
  const [foldersResult, setFoldersResult] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const { cusNavId, mail } = useSelector((state) => state.auth);
  const { selectedDefaultLanguage } = useSelector((state) => state.topBar);

  const [{ loading }, getFolders] = customAreaApi(
    {
      url: `${window.API_V1}/cus/folders/${cusNavId}/${encodeURIComponent(mail)}/search`,
      method: 'POST',
      data: {
        filters: [],
        reduce: {
          limit: 0,
          skip: 0,
        },
      },
    },
    { manual: true }
  );

  const loadCustomFolders = async () => {
    try {
      if (loading) {
        return;
      }
      const {
        data: { errors, data },
      } = await getFolders();
      if (errors.length) {
        throw new Error(convertErrorsArrayToString(errors));
      }
      setFolderRoot(data.find((x) => !x.visible && x.pathId === '1/'));
      const newDta = data.map((d) => {
        const filterI18Name = d.i18List.find((x) => x.i18Code === selectedDefaultLanguage);
        if (filterI18Name) {
          return { ...d, i18Name: filterI18Name.i18Name };
        }
        return { ...d, i18Name: d.i18List[0].i18Name };
      });
      setFolders(
        newDta
          ? newDta
              .sort((a, b) => a.i18Name.localeCompare(b.i18Name))
              .map((folder) =>
                (folder.childsFolders && folder?.childsFolders?.length > 0) ||
                (folder.childs && folder?.childs?.length > 0)
                  ? { ...folder, isOpen: false }
                  : folder
              )
          : []
      );
    } catch (ex) {
      console.error('Error getting custom folders: ', ex);
    }
  };

  useEffect(() => {
    setFolders([]);
    if (!emptyString(selectedDefaultLanguage)) {
      loadCustomFolders();
    }
  }, []);

  useEffect(() => {
    if (!folderSelected && folders.length > 0) {
      const folderSelectedFromArray = folders.find((folder) => folder.id === DEFAULT_FOLDER_ID);
      setFolderSelected(folderSelectedFromArray);
    }
  }, [folderSelected, folders]);

  useEffect(() => {
    if (searchValue) {
      const filteredFoldersIds = new Set();

      folders.forEach((folder) => {
        if (normalizeString(folder.i18Name).includes(normalizeString(searchValue))) {
          const pathIds = folder.pathId.split('/');
          const clearedPathIds = pathIds.filter((x) => x.length > 0);
          clearedPathIds.forEach((id) => {
            filteredFoldersIds.add(id);
          });
        }
      });

      const newFolders = [];
      folders.forEach((folder) => {
        if (filteredFoldersIds.has(folder.id)) {
          newFolders.push({
            ...folder,
            isOpen: true,
          });
        }
      });

      setFoldersResult(newFolders);
    } else {
      setFoldersResult(folders);
    }
  }, [searchValue, folders]);

  const onClickFolder = (folderId) => {
    setFolders(
      foldersResult.map((folder) =>
        folder.id === folderId ? { ...folder, isOpen: !folder.isOpen } : folder
      )
    );
  };

  const getCaretIcon = (folder) => {
    if (
      (folder.childsFolders && folder?.childsFolders?.length > 0) ||
      (folder.childs && folder?.childs?.length > 0)
    ) {
      return folder.isOpen ? (
        <CaretUpIcon className="caret" />
      ) : (
        <CaretDownIcon className="caret" />
      );
    }
    return null;
  };

  const getNameSelectedLanguage = (i18List) => {
    const findName = i18List.find((x) => x.i18Code === selectedDefaultLanguage);
    if (findName && findName?.i18Name !== '') {
      return findName?.i18Name;
    }
    return i18List[0].i18Name;
  };

  const CustomFolderTree = ({ listItem, level }) => {
    return listItem
      ? listItem.map((item) =>
          item.id ? (
            <React.Fragment key={item.id}>
              <CustomFolderTreeWrapper
                className="item-data-container"
                level={level}
                onClick={() => onClickFolder(item.id)}
              >
                <div id={`toggle-custom-folder${item.id}`} className="info-container">
                  <RadioInput
                    type="radio"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFolderSelected(item);
                    }}
                    checked={folderSelected?.id === item.id}
                    onChange={(e) => e.stopPropagation()}
                  />
                  {(item.isOpen && item.childsFolders && item?.childsFolders?.length > 0) ||
                  (item.childs && item?.childs?.length > 0) ? (
                    <OpenFolderIcon className="folder-icon" />
                  ) : (
                    <FolderIcon className="folder-icon" />
                  )}
                  <span className="text-file">{getNameSelectedLanguage(item.i18List)}</span>
                  {getCaretIcon(item)}
                </div>
              </CustomFolderTreeWrapper>
              <Collapse isOpen={item.isOpen}>
                {foldersResult?.some((currentFolder) => {
                  const pathIds = currentFolder.pathId.split('/');
                  const clearedPathIds = pathIds.filter((x) => x.length > 0);
                  const parentId = clearedPathIds[clearedPathIds.length - 2];
                  return parentId === item.id;
                }) ? (
                  <CustomFolderTree
                    listItem={foldersResult.filter((currentFolder) => {
                      const pathIds = currentFolder.pathId.split('/');
                      const clearedPathIds = pathIds.filter((x) => x.length > 0);
                      const parentId = clearedPathIds[clearedPathIds.length - 2];
                      return parentId === item.id;
                    })}
                    level={level + 1}
                  />
                ) : null}
              </Collapse>
            </React.Fragment>
          ) : null
        )
      : null;
  };

  CustomFolderTree.propTypes = {
    listItem: PropTypes.arrayOf(CustomFolderProps).isRequired,
    level: PropTypes.number.isRequired,
  };

  const onChangeSearch = (e) => {
    if (!e?.target?.value) {
      setSearchValue('');
    } else {
      setSearchValue(e.target.value);
    }
  };

  return (
    <CustomFoldersDataWrapper>
      <div className="form h-50 d-flex justify-content-end search-folder-container">
        <TextField
          borderRadius={2}
          className="m-0 h-50 search-folder-input"
          inputClassName="w-100"
          withButtonClear
          rightIcon={<SearchIcon fill={colors.black} />}
          value={searchValue ?? ''}
          onChange={onChangeSearch}
          placeholder={t('documents_upload_destination_search_label')}
        />
      </div>

      <div className="custom-folder-tree">
        <CustomFolderTree
          listItem={foldersResult.filter((folder) => {
            const pathIds = folder.pathId.split('/');
            const clearedPathIds = pathIds.filter((x) => x.length > 0);
            const parentId = clearedPathIds[clearedPathIds.length - 2];
            return parentId === folderRoot.id;
          })}
          level={1}
        />
      </div>

      <Row className="w-100 action-buttons">
        <Col className="d-flex flex-row justify-content-end p-0" sm={12}>
          <Button
            className="mr-3"
            type="button"
            color="secondary"
            label={t('general_button_cancel')}
            onClick={() => {
              setFolderSelected(null);
              onClose();
            }}
          />
          <Button
            type="submit"
            color="primary"
            label={t('general_button_ok')}
            disabled={false}
            onClick={onClose}
          />
        </Col>
      </Row>
    </CustomFoldersDataWrapper>
  );
};

SelectCustomFolder.defaultProps = {
  folderSelected: null,
};

SelectCustomFolder.propTypes = {
  setFolderSelected: PropTypes.func.isRequired,
  folderSelected: PropTypes.shape({
    id: PropTypes.string,
    pathId: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
};

export default SelectCustomFolder;
